import React, { useEffect } from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import clsx from 'clsx';
import { Form, Formik } from 'formik';
import { isEmpty } from 'lodash';
import usePage from '@dashboard/hooks/usePage';
import usePageQueries from '@dashboard/hooks/queries/usePageQueries';
import { FormElement, Label } from '@dashboard/components/Form';
import FormSubmit from '@dashboard/components/Formik/components/FormSubmit';
import { TextInput } from '@dashboard/components/Formik';
import FormError from '@dashboard/components/Formik/components/FormError';
import { RadioGroup } from '@dashboard/components/ui/radio-group';
import { transformApiErrorToFormik } from '@dashboard/services/form';
import ConfirmationCheckoutMessageEditor from './ConfirmationCheckoutMessageEditor';
import SendTestEmailButton from './SendTestEmailButton';
import { usePreview } from '@dashboard/context/PreviewContext';
import { UnsavedChangesReporter } from '@dashboard/components/UnsavedChanges';

const RadioCardItem = ({ value, checked, children }) => (
  <RadioGroupPrimitive.Item
    value={value}
    className={clsx(
      'relative rounded-md px-3 py-2 text-sm font-medium block w-full text-left',
      'border border-input transition-colors outline-none cursor-pointer',
      'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      'data-[state=checked]:border-primary',
      'data-[state=unchecked]:hover:border-gray-300 dark:data-[state=unchecked]:hover:border-input',
    )}
  >
    <div className="flex-1 pr-6">{children}</div>
    <span
      className={clsx(
        'absolute right-2 top-2 flex size-4 items-center justify-center rounded-full border transition-colors',
        checked ? 'border-primary' : 'border-input',
      )}
    >
      <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
        <span className="size-2 rounded-full bg-primary" />
      </RadioGroupPrimitive.Indicator>
    </span>
  </RadioGroupPrimitive.Item>
);

const ReminderEmailForm = ({ reminderNumber }) => {
  const { page, updatePage } = usePage();
  const { sendTestReminderEmail } = usePageQueries();
  const [{ previewPriceId }, previewDispatch] = usePreview();
  const setPreview = value => previewDispatch({ type: 'set', value });

  const sendReminderTestEmail = () =>
    sendTestReminderEmail(page._id, reminderNumber, previewPriceId || undefined);

  const reminderKey = `reminder${reminderNumber}`;
  const reminderData = page?.checkoutAbandonment?.emailReminders?.[reminderKey] || {};

  const defaultSubject = 'Complete your purchase';
  const defaultButtonText = 'Complete order';

  const initialValues = {
    customizeEmail: reminderData.customizeEmail || false,
    subject: reminderData.subject || defaultSubject,
    body: reminderData.body || '',
    buttonText: reminderData.buttonText || defaultButtonText,
  };

  useEffect(() => {
    setPreview({
      type: 'reminderEmailMessage',
      reminderNumber: reminderNumber,
    });
  }, [reminderNumber]);

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={async (values, actions) => {
        actions.setStatus(undefined);

        const payload = {
          checkoutAbandonment: {
            ...page?.checkoutAbandonment,
            emailReminders: {
              ...page?.checkoutAbandonment?.emailReminders,
              [reminderKey]: {
                ...values,
              },
            },
          },
        };

        const { data, error } = await updatePage.mutateAsync(payload);

        if (data) {
          actions.setStatus('');
          const updatedReminderData =
            data.checkoutAbandonment?.emailReminders?.[reminderKey] || values;

          actions.resetForm({
            values: {
              customizeEmail: updatedReminderData.customizeEmail || false,
              subject: updatedReminderData.subject || defaultSubject,
              body: updatedReminderData.body || '',
              buttonText: updatedReminderData.buttonText || defaultButtonText,
            },
          });
        } else {
          const { status, errors } = transformApiErrorToFormik(error);

          if (!isEmpty(errors)) {
            actions.setErrors(errors);
          } else {
            actions.setStatus(status);
          }
        }
      }}
    >
      {({ values, status, dirty, setFieldValue, handleSubmit, setValues, isSubmitting }) => {
        const mode = values.customizeEmail ? 'custom' : 'default';

        return (
          <Form onSubmit={handleSubmit}>
            <UnsavedChangesReporter />
            <RadioGroup
              value={mode}
              onValueChange={value => {
                setValues({
                  ...initialValues,
                  customizeEmail: value === 'custom',
                });
              }}
              className="grid grid-cols-2 gap-2 mt-1"
            >
              <RadioCardItem value="default" checked={mode === 'default'}>
                Reminder email
              </RadioCardItem>
              <RadioCardItem value="custom" checked={mode === 'custom'}>
                Custom reminder email
              </RadioCardItem>
            </RadioGroup>

            {mode === 'default' ? (
              <div className="mt-4">
                <FormElement>{status && status}</FormElement>

                <FormSubmit>
                  <div className="flex flex-wrap gap-y-4 justify-between w-full">
                    <div className="flex items-baseline whitespace-nowrap gap-x-2">
                      <FormSubmit.Button>Save</FormSubmit.Button>
                      <FormSubmit.State visible={!!dirty && !isSubmitting} />
                    </div>

                    <div className="ml-auto flex gap-x-2 items-start">
                      <SendTestEmailButton onSend={sendReminderTestEmail} />
                    </div>
                  </div>
                </FormSubmit>
              </div>
            ) : (
              <div className="mt-4">
                <FormElement>
                  <Label htmlFor={`${reminderKey}-subject`}>Subject</Label>
                  <TextInput
                    required
                    id={`${reminderKey}-subject`}
                    name="subject"
                    type="text"
                    value={values.subject}
                    className="text-sm"
                  />
                  <FormError name="subject" />
                </FormElement>

                <FormElement>
                  <div className="flex justify-between items-baseline">
                    <Label htmlFor={`${reminderKey}-body`} tags={['supports HTML']}>
                      Email body
                    </Label>
                    <span className="text-xs text-muted-foreground font-normal text-right ml-auto">
                      Insert variables by typing{' '}
                      <code className="font-mono bg-muted px-1 py-0.5 text-primary">{'{'}</code>
                    </span>
                  </div>

                  <ConfirmationCheckoutMessageEditor
                    required
                    id={`${reminderKey}-body`}
                    name="body"
                    page={page}
                    values={values}
                    setFieldValue={setFieldValue}
                    reminderEmail={true}
                  />
                  <FormError name="body" />
                </FormElement>

                <FormElement>
                  <Label htmlFor={`${reminderKey}-buttonText`}>Button text</Label>
                  <TextInput
                    required
                    id={`${reminderKey}-buttonText`}
                    name="buttonText"
                    type="text"
                    value={values.buttonText}
                    className="text-sm"
                  />
                  <FormError name="buttonText" />
                </FormElement>

                <FormElement>{status && status}</FormElement>

                <FormSubmit>
                  <div className="flex flex-wrap gap-y-4 justify-between w-full">
                    <div className="flex items-baseline whitespace-nowrap gap-x-2">
                      <FormSubmit.Button>Save</FormSubmit.Button>
                      <FormSubmit.State visible={!!dirty && !isSubmitting} />
                    </div>

                    <div className="ml-auto flex gap-x-2 items-start">
                      <SendTestEmailButton onSend={sendReminderTestEmail} />
                    </div>
                  </div>
                </FormSubmit>
              </div>
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

export default ReminderEmailForm;
